import * as Yup from "yup";

import { Modal } from "components";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";
import {
  UpdateKavaPremiumMemberLimit,
  UpdateKavaPremiumMemberLimitVariables,
} from "_graphql/visal/mutations/__generated__/UpdateKavaPremiumMemberLimit";
import { useReinsurerKavaPremiumLimit } from "hooks/data/use-re-insurers";
import { UPDATE_KAVA_PREMIUM_MEMBER_LIMIT } from "_graphql/visal/mutations/premium-limit";
import CreateLimitForm from "components/forms/create-limit-form";
import { useAppSearch } from "_graphql/cache/auth";

export const limitValidation = Yup.object().shape({
  participationLowerLimit: Yup.number()
    .min(0, "Participation Lower Limit must be at least 0")
    .required("Participation Lower Limit is required"),
});

export default function UpdateLimitContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
  premiumMemberId?: string;
}) {
  const searchParams = useAppSearch();
  const { limit, loading: fetching } = useReinsurerKavaPremiumLimit({
    premiumMemberLimitId: searchParams?.id || "1",
  });

  const [update, { loading }] = useMutation<
    UpdateKavaPremiumMemberLimit,
    UpdateKavaPremiumMemberLimitVariables
  >(UPDATE_KAVA_PREMIUM_MEMBER_LIMIT);

  const form = useFormik<any>({
    initialValues: {
      participationLowerLimit: 0,
    },
    validationSchema: limitValidation,
    onSubmit: async (variables) => {
      await update({
        variables: {
          participationLowerLimit: variables.participationLowerLimit,
          premiumMemberLimitId: limit?.premium_member_limit_id || "",
        },
      }).then(({ data }) => {
        if (data?.updateKavaPremiumMemberLimit) {
          toast.success("Kava Premium Member Limit updated successfully");
          refetch?.();
          form.resetForm();
        } else {
          toast.error("Failed to update Kava Premium Member Limit");
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (limit) {
      form.setValues({
        participationLowerLimit: limit?.participation_lower_limit,
      });
    }
  }, [limit]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={fetching}
      title="Update Kava Premium Limit"
      description="Provide the details to add a new Kava Premium Limit"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Updating Limit..." : "Update Limit"}
          </button>
        </>
      )}
    >
      <CreateLimitForm form={form} />
    </Modal>
  );
}
